import { apiGet, apiPatch, apiPost, apiProjectsGet } from '../../utilities/apiClient';
import {
  ADD_ACCOUNTS,
  ADD_PROJECTS,
  SET_ACCOUNT,
  SET_CURRENT_ACCOUNT,
  FETCHING_ACCOUNTS,
  FETCHED_ACCOUNTS,
  UPDATE_ACCOUNT_FEATURES,
} from '../constants';
import { getStoreState } from './_sessionIds';
import { fetchProjects } from './projects';

export const withoutPartnerAccessMetadata = (account: any): any => {
  if (!account || typeof account !== 'object') return account;
  const {
    via_partner_access: _viaPartnerAccess,
    partner_access_project_id: _partnerAccessProjectId,
    partner_access_conversation_id: _partnerAccessConversationId,
    partner_access_return_account_id: _partnerAccessReturnAccountId,
    partner_access_return_account: _partnerAccessReturnAccount,
    ...clean
  } = account;
  return clean;
};

const resolvePartnerReturnAccount = (
  sourceAccount: any,
  state: any,
  accounts: any[],
): any => {
  if (!sourceAccount?.via_partner_access) return sourceAccount || state.accounts?.primary || null;
  const returnAccountId = sourceAccount.partner_access_return_account_id;
  return sourceAccount.partner_access_return_account
    || (returnAccountId
      ? accounts.find((candidate: any) => candidate?.account_id === returnAccountId)
      : null)
    || state.accounts?.primary
    || accounts.find((candidate: any) => candidate?.is_primary)
    || null;
};

// The workspace a new or restored session opens on. get-accounts marks the
// user's own account current, so partner staff landed on their (usually empty)
// personal account every time; the "a partner lands on their workspace" rule
// only ran on the Switch account screen, which sign-in never shows (ED-2811).
//   1. the workspace this device last used, if the user still belongs to it —
//      never a partner-access client view, which is only entered deliberately;
//   2. otherwise a partner workspace: the user's own first, else one they staff;
//   3. otherwise the server's choice.
export const preferredSessionAccount = (accounts: any[], serverCurrent: any, lastAccount: any): any => {
  // An active "Access plan" client view is held deliberately — fetchAccounts
  // keeps it sticky as current. A session restore that runs mid-handoff must
  // never land away from it, or the partner is bounced back to partner Home.
  if (serverCurrent?.via_partner_access) return serverCurrent;
  const list = Array.isArray(accounts) ? accounts : [];
  if (lastAccount?.account_id && !lastAccount.via_partner_access) {
    const last = list.find((a: any) => a?.account_id === lastAccount.account_id);
    if (last) return last;
  }
  return list.find((a: any) => a?.partner && a?.is_primary)
    || list.find((a: any) => a?.partner)
    || serverCurrent
    || list[0]
    || null;
};

export const updateAccountFeatures = (updates: Record<string, any>) => async (dispatch: any) => {
  const state = getStoreState() as any;
  const accountId = state.session.account?.account_id;
  const cognitoId = state.authentication.user?.cognito_id;
  if (!accountId || !cognitoId) return { success: false };

  // Optimistically update Redux so the UI responds instantly — but capture
  // the prior values first: when the server refuses (e.g. a partner in access
  // mode without membership perms), the flip must roll back, or a feature
  // gate like the medications opt-in unlocks its screen on a FAILED enable
  // (ED-2546 fail-open).
  // Restore the EXACT prior value — including undefined for keys that were
  // never set. Coercing absent keys to false would change semantics for any
  // gate that treats undefined as default-on; spreading undefined back over
  // the merged features leaves the key indistinguishable from absent for
  // every truthiness read.
  const prevFeatures = state.session.account?.features || {};
  const rollback: Record<string, any> = {};
  for (const key of Object.keys(updates)) rollback[key] = prevFeatures[key];

  dispatch({ type: UPDATE_ACCOUNT_FEATURES, payload: updates });

  try {
    const response = await apiPatch(
      `/accounts/update-account-features/${accountId}/${cognitoId}`,
      { updates },
    );
    if (!response || response.success === false) {
      dispatch({ type: UPDATE_ACCOUNT_FEATURES, payload: rollback });
    }
    return response;
  } catch (err: any) {
    dispatch({ type: UPDATE_ACCOUNT_FEATURES, payload: rollback });
    return { success: false, message: err?.message };
  }
};

// Partner-only: switch the active account to a customer the partner manages.
// The partner remains logged in as themselves (their cognito_id is unchanged
// in state.authentication.user), but session.account / accounts.current now
// point at the customer's account so all scoped API calls run against the
// customer's account_id. Mirrors the legacy frontend Access button on
// ProjectCard (frontend/client/src/Components/ProjectCard/index.js#L364).
//
// AppShell re-evaluates partner-vs-customer routing off session.account.partner,
// so dispatching this with a non-partner account_id swaps the user into the
// MainTabNavigator. RootNavigator consumes `partner_access_project_id` and
// installs the exact plan route with one root-targeted state reset. Use
// switchBackToPrimaryAccount() to undo.
export const switchToCustomerAccount =
  (
    targetAccount: { account_id: string; [k: string]: any },
    destination?: { projectId?: number | string; conversationId?: string },
  ) =>
  async (dispatch: any) => {
    if (!targetAccount?.account_id) return { success: false };
    const state = getStoreState() as any;
    const cognitoId = state.authentication?.user?.cognito_id;
    if (!cognitoId) {
      return {
        success: false,
        message: 'Your session could not be verified. Please sign in again.',
      };
    }

    // GATE (ED-2551): a partner must actually hold a membership on the
    // customer account before the shell switches into it. This switch is pure
    // client-side Redux — the server never sees it — and every client-service
    // endpoint independently rejects callers without a membership, so entering
    // without one produced a hollow shell: empty screens, failing writes.
    // Probe with the projects fetch (requires basic-user on the target
    // account); on success it doubles as the pre-warm this action used to
    // fire after the switch, so no extra round-trip is added.
    let probedProjects: any[] | undefined;
    let probe: any;
    try {
      probe = await apiProjectsGet(`/get-all/${targetAccount.account_id}/${cognitoId}`);
    } catch (_) {
      probe = null;
    }
    if (!probe?.success) {
      return {
        success: false,
        message: "You don't have access to this client's account yet — your login has no membership on it.",
      };
    }
    const scopedProjects: any[] = Array.isArray(probe.payload) ? probe.payload : [];
    probedProjects = scopedProjects;
    const belongsToTarget = (project: any): boolean =>
      String(project?.account_id ?? project?.customer_account_id) === String(targetAccount.account_id);
    if (!scopedProjects.every(belongsToTarget)) {
      return {
        success: false,
        message: "This client's plans could not be verified. Please try again.",
      };
    }
    if (
      destination?.projectId != null
      && !scopedProjects.some((project: any) => (
        belongsToTarget(project)
        && String(project?.id) === String(destination.projectId)
      ))
    ) {
      return {
        success: false,
        message: "This plan is not available in the selected client account.",
      };
    }
    // Hydrate the customer's REAL account row before it becomes session.account.
    //
    // Both callers (PartnerPlanDetailScreen, PartnerCustomerDetailScreen) build
    // `targetAccount` by spreading the partners-service customer row, and that
    // row projects the plan as FLAT aliases — `plan_name` / `plan_type` /
    // `plan_value` (partners/controllers/customers/get). It carries no
    // `user_plan`, no `features`, no `subscription`, no `users`. The session
    // reducer handles SET_ACCOUNT by WHOLESALE REPLACEMENT, not a merge, so
    // from the dispatch below onward `session.account.user_plan` is undefined
    // and `usePaidPlan` reads `userPlan?.value` as undefined => hasPaidPlan
    // false. A customer paying every month reads as Free for as long as the
    // partner is inside their account: HistoryScreen hides every conversation
    // older than FREE_HISTORY_DAYS behind "History limited on free plan", every
    // account-feature gate renders off, and useAccountLimits resolves each cap
    // to 0. The paid plan is sitting in the very same object under
    // `plan_value` — the gate just reads a key the partners projection never
    // had.
    //
    // Nothing downstream heals it. fetchAccounts detects `via_partner_access`
    // and takes the sticky branch, which deliberately does NOT re-dispatch
    // SET_CURRENT_ACCOUNT (that is what keeps the partner from being bounced
    // out of the shell), and restoreSession takes its `current` from that same
    // return — so neither a background refetch nor a web reload rehydrates.
    //
    // No new endpoint is needed. The ED-2551 probe above has just proved the
    // partner holds an active membership on this account, and get-accounts
    // selects `WHERE am.cognito_id = $1` (client/utilities/account-utils), so
    // the partner's own accounts fetch already returns the customer's fully
    // hydrated row. Best-effort: a hydration failure falls back to the thin
    // row, which is exactly today's behaviour — it must not block the switch
    // the partner actually asked for.
    let hydrated: any = null;
    if (cognitoId) {
      try {
        const res: any = await apiGet(
          `/accounts/get-accounts/${targetAccount.account_id}/${cognitoId}`,
        );
        if (res?.success && Array.isArray(res.payload)) {
          hydrated = res.payload.find(
            (a: any) => a?.account_id === targetAccount.account_id,
          ) || null;
        }
      } catch (_) {
        hydrated = null;
      }
    }

    const existingList = Array.isArray(state.accounts?.list) ? state.accounts.list : [];
    // Capture the workspace the partner entered FROM before replacing the
    // session account. It is the correct Exit destination even when the user
    // belongs to several accounts and their globally-primary account is not
    // the partner workspace currently in use.
    const sessionSource = state.session?.account?.account_id
      && state.session.account.account_id !== targetAccount.account_id
      ? state.session.account
      : null;
    const currentSource = state.accounts?.current?.account_id
      && state.accounts.current.account_id !== targetAccount.account_id
      ? state.accounts.current
      : null;
    const sourceAccount = sessionSource || currentSource;
    const primary = state.accounts?.primary
      || existingList.find((a: any) => a?.is_primary)
      || existingList[0]
      || sourceAccount
      || null;
    const returnAccount = resolvePartnerReturnAccount(sourceAccount, state, existingList) || primary;

    // Tag the target with via_partner_access from the FIRST dispatch (not just
    // the ADD_ACCOUNTS below), so there's never a window where accounts.current
    // lacks the flag — that flag is the definitive "in customer access" signal
    // AppShell gates the shell on.
    //
    // `targetAccount` is spread LAST on purpose. Its `partner: false`,
    // `status: 'active'` and `permissions` are deliberate caller overrides that
    // shape the access shell, and letting the hydrated row win would silently
    // change what the partner can reach — a separate question from this one.
    // Only the keys the thin row never had come from `hydrated`.
    const taggedTarget = {
      ...withoutPartnerAccessMetadata(hydrated || {}),
      ...withoutPartnerAccessMetadata(targetAccount),
      via_partner_access: true,
      ...(destination?.projectId != null
        ? {
            partner_access_project_id: destination.projectId,
            ...(destination.conversationId
              ? { partner_access_conversation_id: destination.conversationId }
              : {}),
          }
        : {}),
      ...(returnAccount?.account_id
        ? {
            partner_access_return_account_id: returnAccount.account_id,
            partner_access_return_account: returnAccount,
          }
        : {}),
    };
    // Commit the verified list with the account switch. Keeping this below the
    // hydration await prevents a temporary state where client projects are in
    // Redux while the partner workspace is still the active account.
    dispatch({ type: ADD_PROJECTS, payload: scopedProjects });
    dispatch({ type: SET_CURRENT_ACCOUNT, payload: taggedTarget });
    dispatch({ type: SET_ACCOUNT, payload: taggedTarget });

    // Merge the target into accounts.list so AccountsScreen + any other
    // "switch account" surface can offer it as a destination. Without
    // this, the partner has no UI path back out of the customer view —
    // the partner's account_memberships fetch never included the
    // customer (the only reason they can reach it at all is the
    // partner-membership lookup on the server).
    //
    // Dedupe by account_id, tag the target with `via_partner_access`
    // so the UI can render a "Viewing as partner" badge, and preserve
    // the existing primary so switchBackToPrimaryAccount() still
    // resolves the partner's own account.
    const filtered = existingList.filter((a: any) => a?.account_id !== targetAccount.account_id);
    const nextList = [...filtered, taggedTarget];
    dispatch({
      type: ADD_ACCOUNTS,
      payload: { accounts: nextList, current: taggedTarget, primary },
    });

    // (The old post-switch pre-warm lives at the top now — the membership
    // probe already hydrated the customer's project list on success.)
    return { success: true, projects: probedProjects };
  };

// Partner-only: return from a customer view to the partner's own account.
// Used by any "Exit customer view" affordance — falls back to the primary
// account if explicit `primary` isn't set yet. Starts a best-effort project
// pre-warm without making the Exit control wait on that network request.
export const switchBackToPrimaryAccount = () => async (dispatch: any) => {
  const state = getStoreState() as any;
  const cognitoId = state.authentication?.user?.cognito_id;
  const current = state.accounts?.current || state.session?.account;
  const accounts = Array.isArray(state.accounts?.list) ? state.accounts.list : [];
  const returnAccountId = current?.partner_access_return_account_id;
  const storedReturn = current?.partner_access_return_account;
  const primary = storedReturn
    || (returnAccountId
      ? accounts.find((a: any) => a?.account_id === returnAccountId)
      : null)
    || state.accounts?.primary
    || accounts.find((a: any) => a?.is_primary)
    || accounts.find((a: any) => a?.partner)
    || accounts[0]
    || null;
  if (!primary?.account_id) return { success: false };
  dispatch({ type: SET_CURRENT_ACCOUNT, payload: primary });
  dispatch({ type: SET_ACCOUNT, payload: primary });
  // switchToCustomerAccount merged the client into accounts.list WITH the access
  // flag. Left there, picking it later from Switch account put the green
  // "Viewing … account" banner back up outside any access session, skipping the
  // membership probe (ED-2811). Keep the row, drop the flag.
  if (accounts.some((a: any) => a?.via_partner_access)) {
    dispatch({
      type: ADD_ACCOUNTS,
      payload: {
        accounts: accounts.map(withoutPartnerAccessMetadata),
        current: primary,
        primary: state.accounts?.primary || primary,
      },
    });
  }
  if (cognitoId) {
    Promise.resolve(dispatch(fetchProjects(primary.account_id, cognitoId))).catch(() => undefined);
  }
  return { success: true };
};

export const fetchAccounts = (accountId: string, cognitoId: string) => async (dispatch: any) => {
  // While a partner is inside a customer's account via "Access plan", the sticky
  // `via_partner_access` on accounts.current is what holds the customer shell up
  // (AppShell gates on it). This refetch runs with the PARTNER's own credentials
  // and returns the partner's accounts (no flag) — letting it overwrite
  // accounts.current strips the flag, AppShell recomputes, and the shell bounces
  // back to partner Home (esp. on web, where a nav reset briefly remounts
  // AccountsScreen and fires this). So while the flag is set, preserve the
  // customer account as `current`; only Exit (switchBackToPrimaryAccount) clears it.
  const stickyCurrent = (getStoreState() as any).accounts?.current;
  const inCustomerAccess = !!stickyCurrent?.via_partner_access;
  dispatch({ type: FETCHING_ACCOUNTS });
  try {
    const response = await apiGet(`/accounts/get-accounts/${accountId}/${cognitoId}`);
    if (response?.success) {
      const accounts = (response.payload || []) as any[];
      // Mirror legacy frontend/client/src/store/actions/accounts.js: prefer
      // the account flagged is_current (the one we asked for in the URL),
      // fall back to is_primary (the user's own account, where account_id ===
      // their cognito_id), and only then to accounts[0]. Picking accounts[0]
      // blindly was a latent bug — Postgres' DISTINCT ON returns rows in an
      // unspecified order, so users with multiple memberships (e.g. their
      // personal account + an eleplan-admin account) sometimes landed on
      // the wrong default and saw "not authorized" errors against admin
      // endpoints because the URL's account_id didn't match the account
      // their eleplan-admin permission lived on.
      const fallback = Array.isArray(accounts) ? accounts[0] : null;
      const primary = accounts.find((a: any) => a?.is_primary) || fallback;

      if (inCustomerAccess) {
        // Keep the customer account as `current` (and in the list) — refresh the
        // rest — so a background fetch can't knock the partner out of the plan.
        const mergedList = [...accounts.filter((a: any) => a?.account_id !== stickyCurrent.account_id), stickyCurrent];
        dispatch({ type: ADD_ACCOUNTS, payload: { accounts: mergedList, current: stickyCurrent, primary } });
        dispatch({ type: FETCHED_ACCOUNTS });
        return { success: true, accounts: mergedList, current: stickyCurrent, primary, account_closed: !!response.account_closed };
      }

      const current =
        accounts.find((a: any) => a?.is_current) ||
        accounts.find((a: any) => a?.is_primary) ||
        fallback;
      dispatch({ type: ADD_ACCOUNTS, payload: { accounts, current, primary } });
      if (current) dispatch({ type: SET_CURRENT_ACCOUNT, payload: current });
      dispatch({ type: FETCHED_ACCOUNTS });
      // `account_closed` is set by get-accounts when there are no active
      // accounts but a status='closed' one exists — the login flow uses it to
      // show a "closed" screen + sign-out instead of re-provisioning.
      return { success: true, accounts, current, primary, account_closed: !!response.account_closed };
    }
    dispatch({ type: FETCHED_ACCOUNTS });
    // Distinguish a definitive failure (auth rejection / not-found) from a
    // transient one (5xx, or a missing status). restoreSession uses `transient`
    // to decide whether to keep a valid session on a boot hiccup vs. log out.
    const status = (response as any)?.status;
    // An explicit `transient` from the api layer WINS. apiClient returns
    // { status: 0, transient: true } when the token refresh could not even be
    // attempted (offline, or Cognito unreachable while the app cold-starts
    // before the network is up). Recomputing purely from `status` read that 0
    // as a definitive rejection — 0 is a number and 0 < 500 — so restoreSession
    // saw "your account list is definitively empty", dispatched IS_LOGGED_OUT,
    // and put a valid user on the login screen with a refresh token good for
    // another 89 days.
    //
    // That is the same misclassification #2267 fixed one layer up: it stopped
    // performRefresh handing back an expired token and introduced this
    // transient signal, but the signal was overwritten here, so the logout
    // survived. Only reachable once per 24h ID-token lifetime, which is why it
    // presented as "opened the app the next day and had to sign in again".
    //
    // status 0 is treated as transient in its own right too: it means no HTTP
    // response happened at all, which is never a server verdict on the session.
    //
    // 429 and 408 are transient too, and 429 is not a hypothetical here: the
    // stage throttle is 10 req/s AGGREGATE across every client
    // (resources/http-api-throttling.yml), so a burst of other people's traffic
    // can rate-limit you. Reading that as a verdict on YOUR session signs you
    // out because someone else was busy.
    const transient = (response as any)?.transient === true
      || typeof status !== 'number'
      || status === 0
      || status === 408
      || status === 429
      || status >= 500;
    return { success: false, status, transient };
  } catch (err: any) {
    dispatch({ type: FETCHED_ACCOUNTS });
    // A thrown error here is a network failure (offline / DNS / CORS) — always
    // transient; never log a valid user out over a connectivity blip.
    return { success: false, message: err?.message, transient: true };
  }
};

/**
 * Put the authenticated app into the account that owns a desktop-handoff plan,
 * and prove that the exact plan is visible before navigation is allowed.
 *
 * Browser and Electron sessions persist their active account independently.
 * Navigating first would make every downstream request use the desktop's stale
 * account id; on a bare ProjectChat route, that historically also allowed the
 * screen to fall back to another account's first plan.
 */
export const activateDesktopHandoffAccount = (
  targetAccountId: string,
  projectId: number | string,
  conversationId?: string,
  options?: { shouldCommit?: () => boolean },
) => async (dispatch: any): Promise<{
  success: boolean;
  message?: string;
  partnerNavigationPending?: boolean;
  cancelled?: boolean;
}> => {
  const ownsTargetProject = (projects: unknown): boolean =>
    Array.isArray(projects)
    && projects.some((project: any) => (
      String(project?.id) === String(projectId)
      && String(project?.account_id ?? project?.customer_account_id) === targetAccountId
    ));

  const state = getStoreState() as any;
  const cognitoId = state.authentication?.user?.cognito_id;
  if (!targetAccountId || !cognitoId) {
    return { success: false, message: 'Sign in to open this desktop chat.' };
  }

  const current = state.accounts?.current || state.session?.account;
  const list = Array.isArray(state.accounts?.list) ? state.accounts.list : [];
  const sourceAccountId = current?.account_id;
  const startedAuthorized = state.session?.authorized;
  const mayCommit = (): boolean => {
    const latest = getStoreState() as any;
    const latestCurrent = latest.accounts?.current || latest.session?.account;
    if (latest.authentication?.user?.cognito_id !== cognitoId) return false;
    if (startedAuthorized === true && latest.session?.authorized !== true) return false;
    if (latestCurrent?.account_id !== sourceAccountId) return false;
    return options?.shouldCommit ? options.shouldCommit() : true;
  };
  const cancelled = () => ({ success: false, cancelled: true });
  const listedTarget = list.find((candidate: any) => candidate?.account_id === targetAccountId) || null;
  let target = current?.account_id === targetAccountId ? current : null;
  try {
    const hydrated: any = await apiGet(
      `/accounts/get-accounts/${targetAccountId}/${cognitoId}`,
    );
    if (hydrated?.success && Array.isArray(hydrated.payload)) {
      const exact = hydrated.payload.find((candidate: any) => candidate?.account_id === targetAccountId);
      if (exact) {
        target = {
          ...withoutPartnerAccessMetadata(listedTarget || {}),
          ...withoutPartnerAccessMetadata(exact),
        };
      }
    }
  } catch (_) {
    target = null;
  }
  if (!mayCommit()) return cancelled();
  if (!target) {
    return { success: false, message: 'This account is not available to your desktop login.' };
  }

  let projects: any[] = [];
  try {
    const response: any = await apiProjectsGet(`/get-all/${targetAccountId}/${cognitoId}`);
    projects = response?.success && Array.isArray(response.payload) ? response.payload : [];
  } catch (_) {
    projects = [];
  }
  if (!mayCommit()) return cancelled();
  if (!ownsTargetProject(projects)) {
    return { success: false, message: 'This plan is not available to your desktop login.' };
  }

  const returnAccount = resolvePartnerReturnAccount(current, state, list);
  const sourceIsPartner = returnAccount?.partner === true
    || state.accounts?.primary?.partner === true;
  const inPartnerAccess = current?.via_partner_access === true;

  if (current?.account_id === targetAccountId && !inPartnerAccess) {
    dispatch({ type: ADD_PROJECTS, payload: projects });
    return { success: true };
  }

  if (sourceIsPartner && target?.partner !== true) {
    const taggedTarget = {
      ...withoutPartnerAccessMetadata(target),
      via_partner_access: true,
      partner_access_project_id: projectId,
      ...(conversationId ? { partner_access_conversation_id: conversationId } : {}),
      ...(returnAccount?.account_id
        ? {
            partner_access_return_account_id: returnAccount.account_id,
            partner_access_return_account: returnAccount,
          }
        : {}),
    };
    if (!mayCommit()) return cancelled();
    dispatch({ type: SET_CURRENT_ACCOUNT, payload: taggedTarget });
    dispatch({ type: SET_ACCOUNT, payload: taggedTarget });
    const primary = state.accounts?.primary
      || list.find((candidate: any) => candidate?.is_primary)
      || returnAccount
      || taggedTarget;
    const nextList = [
      ...list.filter((candidate: any) => candidate?.account_id !== targetAccountId),
      taggedTarget,
    ];
    dispatch({
      type: ADD_ACCOUNTS,
      payload: { accounts: nextList, current: taggedTarget, primary },
    });
    dispatch({ type: ADD_PROJECTS, payload: projects });
    return { success: true, partnerNavigationPending: true };
  }

  if (!mayCommit()) return cancelled();
  dispatch({ type: SET_CURRENT_ACCOUNT, payload: target });
  dispatch({ type: SET_ACCOUNT, payload: target });
  const primary = state.accounts?.primary
    || list.find((candidate: any) => candidate?.is_primary)
    || current
    || target;
  const nextList = [
    ...list.filter((candidate: any) => candidate?.account_id !== targetAccountId),
    target,
  ];
  dispatch({
    type: ADD_ACCOUNTS,
    payload: { accounts: nextList, current: target, primary },
  });
  dispatch({ type: ADD_PROJECTS, payload: projects });
  return { success: true };
};

/**
 * Close (deactivate) the caller's own account — reversible status flip to
 * 'closed' server-side. The caller should sign the user out on success.
 *
 * The server stops the billing before it closes anything, and refuses to close
 * if it can't (ED-2630) — so a failure here means the account is still open and
 * still billing, which is the recoverable state. Surface the message rather
 * than signing the user out.
 *
 * `appleSubscriptionActive` is the one case the server cannot resolve: Apple
 * owns that lifecycle, so the subscription keeps renewing until the customer
 * cancels it in Apple's own settings. Tell them.
 */
export const closeMyAccount = async (
  accountId: string,
  cognitoId: string,
): Promise<{ success: boolean; message?: string; appleSubscriptionActive?: boolean }> => {
  if (!accountId || !cognitoId) return { success: false, message: 'Missing account identifiers' };
  try {
    const res = await apiPost(`/accounts/close/${accountId}/${cognitoId}`, {});
    if (res?.success) {
      return { success: true, appleSubscriptionActive: !!res?.payload?.appleSubscriptionActive };
    }
    return { success: false, message: res?.message || 'Could not close the account.' };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not close the account.' };
  }
};
